import { useEffect, useRef } from "react"
import { useLocation } from "react-router-dom"
import {
  routeFamily,
  trackAssistantEvent,
  wasAssistantNavigation
} from "../utils/assistant-analytics"
import type { StaffChatMessage } from "./use-staff-chat-stream"

/** How long after an unresolved turn a page change still counts as fallback. */
const FALLBACK_WINDOW_MS = 10 * 60_000

/**
 * Why the latest finished assistant turn did not resolve the user's request,
 * or null when it did. "declined" mirrors the agent's own definition: it
 * answered without using a single tool, form, or action.
 */
export function unresolvedReason(
  message: StaffChatMessage | undefined
): "error" | "declined" | null {
  if (!message || message.role !== "assistant") return null
  if (message.error) return "error"
  const usedAnything =
    message.toolEvents.length > 0 ||
    message.pendingActions.length > 0 ||
    message.formRequests.length > 0
  return usedAnything ? null : "declined"
}

/**
 * Implicit signal (contract 1.1.0): the user left for a conventional page
 * right after the assistant failed or declined — i.e. went to do it by hand.
 * Recorded as telemetry, never as negative feedback. One event per
 * unresolved turn; following the assistant's own links doesn't count.
 */
export function useAssistantFallbackTracking(
  messages: StaffChatMessage[],
  isStreaming: boolean
): void {
  const { pathname } = useLocation()
  const unresolved = useRef<{
    id: string
    reason: "error" | "declined"
    at: number
  } | null>(null)
  const reported = useRef<string | null>(null)
  const lastPath = useRef(pathname)

  useEffect(() => {
    if (isStreaming) return
    const last = messages[messages.length - 1]
    const reason = unresolvedReason(last)
    if (!last || !reason) {
      unresolved.current = null
    } else if (unresolved.current?.id !== last.id) {
      unresolved.current = { id: last.id, reason, at: Date.now() }
    }
  }, [messages, isStreaming])

  useEffect(() => {
    if (pathname === lastPath.current) return
    lastPath.current = pathname
    const turn = unresolved.current
    if (
      !turn ||
      reported.current === turn.id ||
      pathname.startsWith("/staff-ai") ||
      wasAssistantNavigation() ||
      Date.now() - turn.at > FALLBACK_WINDOW_MS
    ) {
      return
    }
    reported.current = turn.id
    trackAssistantEvent("fallback_navigation", {
      to: routeFamily(pathname),
      reason: turn.reason
    })
  }, [pathname])
}
